import React, { useMemo, useState, useEffect } from 'react';
import styled from 'styled-components';
import { colors, spacing, borderRadius, typography } from '../theme/colors';

const formatAmount = (amount) => {
  if (!amount && amount !== 0) return '0.00';
  return Number(amount).toLocaleString('en-PK', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
// A typed amount ("25,000") as a number, or null when the text is not one.
const typedAmount = (q) => {
  const t = String(q || '').replace(/[,\s]/g, '');
  if (!/^\d+(\.\d+)?$/.test(t)) return null;
  return Number(t);
};
// WHT is charged only on the part the customer actually paid (allocated − written off).
const calcWht = (amount, adj, rate) => r2(Math.max(0, (Number(amount) || 0) - (Number(adj) || 0)) * ((Number(rate) || 0) / 100));

/**
 * Invoice allocation table — shared by Bank/Cash Receipts, Bank/Cash Payments
 * and the Journal Voucher.
 *
 * `showDeductions` (receipts only): each allocated row gets WHT % / WHT Amt /
 * Adjustment (Bad Debt) / Net. "Allocate" stays the GROSS amount cleared from
 * the invoice; allocations then also carry whtRate / whtAmount / adjustmentAmount.
 * Without the prop the table behaves exactly as before (same allocation objects).
 */
const InvoiceAllocationTable = ({
  invoices, totalReceiptAmount, allocations, onAllocationsChange, currencyCode = 'PKR',
  showDeductions = false,
  // Payments: `variant="payment"` switches labels; `defaultWhtRate` = vendor's
  // WHT rate pre-filled on newly allocated rows; `extraWht` = advance WHT on the
  // unallocated part (included in Total WHT / Net). Receipts pass none → unchanged.
  variant = 'receipt', defaultWhtRate = 0, extraWht = 0
}) => {
  const isPayment = variant === 'payment';

  // ── Filters (2026-10-13): search / date range / allocated, sort by date or
  // balance. Only narrows the list on screen — nothing is read again. Invoices
  // already allocated stay listed whatever the search or dates, so their
  // amounts are never out of sight.
  const [fq, setFq] = useState('');
  const [fFrom, setFFrom] = useState('');
  const [fTo, setFTo] = useState('');
  const [fShow, setFShow] = useState('all'); // all | allocated | open
  const [sortBy, setSortBy] = useState({ key: 'date', dir: 'asc' });
  const idKey = (invoices || []).map(i => i.id).join('|');
  useEffect(() => { // another party's invoices → start with no filter
    setFq(''); setFFrom(''); setFTo(''); setFShow('all'); setSortBy({ key: 'date', dir: 'asc' });
  }, [idKey]);
  const allocatedIds = useMemo(() => new Set((allocations || []).filter(a => Number(a.allocatedAmount) > 0).map(a => a.invoiceId)), [allocations]);
  const filterActive = !!(fq.trim() || fFrom || fTo || fShow !== 'all');
  const showOk = (inv) => (fShow === 'allocated' ? allocatedIds.has(inv.id) : fShow === 'open' ? !allocatedIds.has(inv.id) : true);
  const textDateOk = (inv) => {
    const d = inv.docDate || '';
    if (fFrom && d < fFrom) return false;
    if (fTo && d > fTo) return false;
    const q = fq.trim().toLowerCase();
    if (!q) return true;
    if (String(inv.docNumber || '').toLowerCase().includes(q)) return true;
    if (String(inv.vendorInvoiceNo || '').toLowerCase().includes(q)) return true;
    const amt = typedAmount(q);
    return amt !== null && (Math.abs((Number(inv.totalAmount) || 0) - amt) < 0.5 || Math.abs((Number(inv.balanceAmount) || 0) - amt) < 0.5);
  };
  // Invoices the filter picks (Auto Allocate works on these).
  const matching = useMemo(() => (invoices || []).filter(inv => showOk(inv) && textDateOk(inv)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [invoices, fq, fFrom, fTo, fShow, allocatedIds]);
  const shownInvoices = useMemo(() => {
    const list = (invoices || []).filter(inv => showOk(inv) && (textDateOk(inv) || allocatedIds.has(inv.id)));
    const dir = sortBy.dir === 'desc' ? -1 : 1;
    if (sortBy.key === 'balance') return list.slice().sort((a, b) => dir * ((Number(a.balanceAmount) || 0) - (Number(b.balanceAmount) || 0)));
    return list.slice().sort((a, b) => dir * (a.docDate || '').localeCompare(b.docDate || ''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoices, fq, fFrom, fTo, fShow, allocatedIds, sortBy]);
  const shownBalance = useMemo(() => shownInvoices.reduce((s, i) => s + (Number(i.balanceAmount) || 0), 0), [shownInvoices]);
  const toggleSort = (key) => setSortBy(prev => (prev.key === key
    ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
    : { key, dir: key === 'balance' ? 'desc' : 'asc' }));
  const sortMark = (key) => (sortBy.key === key ? (sortBy.dir === 'asc' ? ' ▲' : ' ▼') : '');
  const clearFilters = () => { setFq(''); setFFrom(''); setFTo(''); setFShow('all'); };
  const L = isPayment
    ? { adj: 'Adjustment / Discount', amount: 'Payment Amount (gross)', totalAdj: 'Total Adjustment', net: 'Net Paid (cash)', invoiceDoc: 'Purchase Invoice' }
    : { adj: 'Adjustment / Bad Debt', amount: 'Receipt Amount (gross)', totalAdj: 'Total Bad Debt / Adj.', net: 'Net Received (cash)', invoiceDoc: 'Sale Invoice' };
  const advWht = showDeductions ? r2(extraWht) : 0;
  const totalAllocated = useMemo(() => {
    return allocations.reduce((sum, a) => sum + (Number(a.allocatedAmount) || 0), 0);
  }, [allocations]);

  const deductionTotals = useMemo(() => {
    if (!showDeductions) return { wht: 0, adj: 0 };
    return allocations.reduce((t, a) => ({
      wht: t.wht + (Number(a.whtAmount) || 0),
      adj: t.adj + (Number(a.adjustmentAmount) || 0)
    }), { wht: 0, adj: 0 });
  }, [allocations, showDeductions]);

  const unallocated = Math.round((Number(totalReceiptAmount) - totalAllocated) * 100) / 100;

  const handleAllocationChange = (invoiceId, docNumber, value) => {
    const invoice = invoices.find(inv => inv.id === invoiceId);
    if (!invoice) return;

    const amount = Math.min(Math.max(0, Number(value) || 0), invoice.balanceAmount);

    const newAllocations = allocations.filter(a => a.invoiceId !== invoiceId);
    if (amount > 0) {
      if (showDeductions) {
        // Keep the row's WHT % / write-off when the amount changes; re-fit them.
        const existing = allocations.find(a => a.invoiceId === invoiceId);
        const prev = existing || {};
        const adj = Math.min(Number(prev.adjustmentAmount) || 0, amount);
        // A newly allocated row starts at the party's default WHT rate (payments:
        // vendor master); an existing row keeps whatever rate it already has.
        const rate = existing ? (Number(prev.whtRate) || 0) : (Number(defaultWhtRate) || 0);
        const wht = rate > 0 ? calcWht(amount, adj, rate) : Math.min(Number(prev.whtAmount) || 0, r2(amount - adj));
        newAllocations.push({ invoiceId, docNumber, allocatedAmount: amount, whtRate: rate, whtAmount: wht, adjustmentAmount: r2(adj) });
      } else {
        newAllocations.push({ invoiceId, docNumber, allocatedAmount: amount });
      }
    }
    onAllocationsChange(newAllocations);
  };

  // Receipts only: edit one deduction field on an already-allocated row.
  const handleDeductionChange = (invoiceId, field, value) => {
    const prev = allocations.find(a => a.invoiceId === invoiceId);
    if (!prev) return;
    const amount = Number(prev.allocatedAmount) || 0;
    let rate = Number(prev.whtRate) || 0;
    let adj = Number(prev.adjustmentAmount) || 0;
    let wht = Number(prev.whtAmount) || 0;

    if (field === 'whtRate') {
      rate = Math.min(Math.max(0, Number(value) || 0), 100);
      wht = calcWht(amount, adj, rate);
    } else if (field === 'whtAmount') {
      // Manual WHT (to match the customer's certificate) — rate kept as typed.
      wht = Math.min(Math.max(0, r2(value)), r2(amount - adj));
    } else if (field === 'adjustmentAmount') {
      adj = Math.min(Math.max(0, r2(value)), amount);
      wht = rate > 0 ? calcWht(amount, adj, rate) : Math.min(wht, r2(amount - adj));
    }
    onAllocationsChange(allocations.map(a => a.invoiceId === invoiceId
      ? { ...a, whtRate: rate, whtAmount: r2(wht), adjustmentAmount: r2(adj) }
      : a));
  };

  const handleAutoAllocate = () => {
    let remaining = Number(totalReceiptAmount) || 0;
    const newAllocations = [];

    // With a filter on, only the invoices it picks are filled; amounts already
    // on other invoices stay and come off the amount first. No filter → as before.
    const pool = filterActive ? matching : invoices;
    if (filterActive) {
      const inPool = new Set(pool.map(i => i.id));
      allocations.filter(a => !inPool.has(a.invoiceId)).forEach(a => {
        newAllocations.push(a);
        remaining = Math.round((remaining - (Number(a.allocatedAmount) || 0)) * 100) / 100;
      });
    }
    const fifo = pool.slice().sort((a, b) => (a.docDate || '').localeCompare(b.docDate || ''));

    // FIFO: oldest invoices first
    for (const inv of fifo) {
      if (remaining <= 0) break;
      const allocAmount = Math.min(remaining, inv.balanceAmount);
      if (allocAmount > 0) {
        const allocatedAmount = Math.round(allocAmount * 100) / 100;
        if (showDeductions) {
          // Carry over any WHT % / write-off already typed on this invoice.
          const existing = allocations.find(a => a.invoiceId === inv.id);
          const prev = existing || {};
          const adj = Math.min(Number(prev.adjustmentAmount) || 0, allocatedAmount);
          const rate = existing ? (Number(prev.whtRate) || 0) : (Number(defaultWhtRate) || 0);
          const wht = rate > 0 ? calcWht(allocatedAmount, adj, rate) : Math.min(Number(prev.whtAmount) || 0, r2(allocatedAmount - adj));
          newAllocations.push({ invoiceId: inv.id, docNumber: inv.docNumber, allocatedAmount, whtRate: rate, whtAmount: wht, adjustmentAmount: r2(adj) });
        } else {
          newAllocations.push({
            invoiceId: inv.id,
            docNumber: inv.docNumber,
            allocatedAmount
          });
        }
        remaining = Math.round((remaining - allocAmount) * 100) / 100;
      }
    }

    onAllocationsChange(newAllocations);
  };

  const handleClearAll = () => {
    onAllocationsChange([]);
  };

  const getAllocation = (invoiceId) => {
    const alloc = allocations.find(a => a.invoiceId === invoiceId);
    return alloc ? alloc.allocatedAmount : 0;
  };

  if (!invoices || invoices.length === 0) {
    return (
      <EmptyMsg>No pending invoices found for this party.</EmptyMsg>
    );
  }

  return (
    <Container>
      <TableHeader>
        <Title>Invoice Allocation</Title>
        <ButtonGroup>
          <AllocBtn type="button" data-auto-allocate onClick={handleAutoAllocate} disabled={!totalReceiptAmount || totalReceiptAmount <= 0}
            title={filterActive ? 'Fill the invoices shown by the filter, oldest first' : 'Fill invoices oldest first'}>
            {filterActive ? 'Auto Allocate (shown)' : 'Auto Allocate'}
          </AllocBtn>
          <ClearBtn type="button" onClick={handleClearAll} disabled={allocations.length === 0}>
            Clear All
          </ClearBtn>
        </ButtonGroup>
      </TableHeader>

      {invoices.length > 0 && (
        <FilterBar data-alloc-filter>
          <FilterInput
            type="text" value={fq} onChange={e => setFq(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') e.preventDefault(); }}
            placeholder={isPayment ? 'Invoice no, vendor bill no or amount' : 'Invoice no or amount'}
            data-alloc-search style={{ flex: '1 1 200px' }}
          />
          <FilterLabel>From<FilterInput type="date" value={fFrom} onChange={e => setFFrom(e.target.value)} data-alloc-from /></FilterLabel>
          <FilterLabel>To<FilterInput type="date" value={fTo} onChange={e => setFTo(e.target.value)} data-alloc-to /></FilterLabel>
          <FilterSelect value={fShow} onChange={e => setFShow(e.target.value)} data-alloc-show>
            <option value="all">All invoices</option>
            <option value="allocated">Allocated only</option>
            <option value="open">Not allocated</option>
          </FilterSelect>
          {filterActive && <ClearBtn type="button" onClick={clearFilters} data-alloc-clear>Clear filters</ClearBtn>}
          <FilterCount data-alloc-count>
            Showing {shownInvoices.length} of {invoices.length} invoices · Balance {currencyCode} {formatAmount(shownBalance)}
          </FilterCount>
        </FilterBar>
      )}
      <TableWrap>
        <Table>
          <thead>
            <tr>
              <Th>Invoice No</Th>
              <Th $click onClick={() => toggleSort('date')} title="Sort by date" data-sort-date>Date{sortMark('date')}</Th>
              <Th style={{ textAlign: 'right' }}>Invoice Amount</Th>
              <Th $click style={{ textAlign: 'right' }} onClick={() => toggleSort('balance')} title="Sort by balance" data-sort-balance>Balance{sortMark('balance')}</Th>
              <Th style={{ textAlign: 'right', width: 150 }}>Allocate Amount</Th>
              {showDeductions && (
                <>
                  <Th style={{ textAlign: 'right', width: 80 }}>WHT %</Th>
                  <Th style={{ textAlign: 'right', width: 110 }}>WHT Amt</Th>
                  <Th style={{ textAlign: 'right', width: 130 }}>{L.adj}</Th>
                  <Th style={{ textAlign: 'right', width: 110 }}>Net</Th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {shownInvoices.length === 0 && (
              <tr><Td colSpan={showDeductions ? 9 : 5} style={{ textAlign: 'center', color: colors.text.tertiary, padding: 14 }}>No invoice matches the filter.</Td></tr>
            )}
            {shownInvoices.map(inv => {
              const allocated = getAllocation(inv.id);
              const alloc = showDeductions ? (allocations.find(a => a.invoiceId === inv.id) || null) : null;
              // Sale Invoice already deducted WHT ("deduct" direction) — entering it
              // again here would double-count WHT Receivable.
              const invoiceHasWht = showDeductions && Number(inv.whtAmount) > 0 && inv.whtAddToTotal !== true;
              const rowNet = alloc ? r2((Number(alloc.allocatedAmount) || 0) - (Number(alloc.whtAmount) || 0) - (Number(alloc.adjustmentAmount) || 0)) : 0;
              return (
                <Tr key={inv.id} $allocated={allocated > 0}>
                  <Td>
                    <strong>{inv.docNumber}</strong>
                    {inv.vendorInvoiceNo && <BillNo title="Vendor's bill no">Bill: {inv.vendorInvoiceNo}</BillNo>}
                    {invoiceHasWht && (
                      <InvWhtTag title={`WHT of ${formatAmount(inv.whtAmount)} was already deducted on this ${L.invoiceDoc}. Don't enter WHT again here.`}>
                        WHT on invoice
                      </InvWhtTag>
                    )}
                  </Td>
                  <Td>{inv.docDate}</Td>
                  <Td style={{ textAlign: 'right' }}>{formatAmount(inv.totalAmount)}</Td>
                  <Td style={{ textAlign: 'right' }}>{formatAmount(inv.balanceAmount)}</Td>
                  <Td style={{ textAlign: 'right' }}>
                    <AllocInput
                      type="number"
                      min="0"
                      max={inv.balanceAmount}
                      step="0.01"
                      value={allocated || ''}
                      onChange={e => handleAllocationChange(inv.id, inv.docNumber, e.target.value)}
                      placeholder="0.00"
                    />
                  </Td>
                  {showDeductions && (
                    <>
                      <Td style={{ textAlign: 'right' }}>
                        <SmallInput
                          type="number" min="0" max="100" step="0.1"
                          disabled={!alloc}
                          value={alloc && Number(alloc.whtRate) > 0 ? alloc.whtRate : ''}
                          onChange={e => handleDeductionChange(inv.id, 'whtRate', e.target.value)}
                          placeholder="0"
                        />
                      </Td>
                      <Td style={{ textAlign: 'right' }}>
                        <MidInput
                          type="number" min="0" step="0.01"
                          disabled={!alloc}
                          value={alloc && Number(alloc.whtAmount) > 0 ? alloc.whtAmount : ''}
                          onChange={e => handleDeductionChange(inv.id, 'whtAmount', e.target.value)}
                          placeholder="0.00"
                        />
                      </Td>
                      <Td style={{ textAlign: 'right' }}>
                        <MidInput
                          type="number" min="0" step="0.01"
                          disabled={!alloc}
                          value={alloc && Number(alloc.adjustmentAmount) > 0 ? alloc.adjustmentAmount : ''}
                          onChange={e => handleDeductionChange(inv.id, 'adjustmentAmount', e.target.value)}
                          placeholder="0.00"
                        />
                      </Td>
                      <Td style={{ textAlign: 'right', fontWeight: 600 }}>{alloc ? formatAmount(rowNet) : '-'}</Td>
                    </>
                  )}
                </Tr>
              );
            })}
          </tbody>
        </Table>
      </TableWrap>

      <SummaryRow>
        <SummaryItem>
          <SummaryLabel>{showDeductions ? L.amount : 'Receipt Amount'}</SummaryLabel>
          <SummaryValue>{currencyCode} {formatAmount(totalReceiptAmount)}</SummaryValue>
        </SummaryItem>
        <SummaryItem>
          <SummaryLabel>Total Allocated</SummaryLabel>
          <SummaryValue $warning={totalAllocated > Number(totalReceiptAmount)}>
            {currencyCode} {formatAmount(totalAllocated)}
          </SummaryValue>
        </SummaryItem>
        {showDeductions && (
          <>
            <SummaryItem>
              <SummaryLabel>{advWht > 0 ? 'Total WHT (incl. advance)' : 'Total WHT'}</SummaryLabel>
              <SummaryValue $muted={r2(deductionTotals.wht + advWht) === 0}>{currencyCode} {formatAmount(r2(deductionTotals.wht + advWht))}</SummaryValue>
            </SummaryItem>
            <SummaryItem>
              <SummaryLabel>{L.totalAdj}</SummaryLabel>
              <SummaryValue $muted={deductionTotals.adj === 0}>{currencyCode} {formatAmount(deductionTotals.adj)}</SummaryValue>
            </SummaryItem>
            <SummaryItem>
              <SummaryLabel>{L.net}</SummaryLabel>
              <SummaryValue>{currencyCode} {formatAmount(r2(Number(totalReceiptAmount) - deductionTotals.wht - deductionTotals.adj - advWht))}</SummaryValue>
            </SummaryItem>
          </>
        )}
        <SummaryItem>
          <SummaryLabel>Unallocated</SummaryLabel>
          <SummaryValue $muted={unallocated === 0} $warning={unallocated < 0}>
            {currencyCode} {formatAmount(unallocated)}
          </SummaryValue>
        </SummaryItem>
      </SummaryRow>

      {totalAllocated > Number(totalReceiptAmount) && (
        <WarningMsg>Total allocated exceeds receipt amount!</WarningMsg>
      )}
    </Container>
  );
};

// ===== STYLED =====
const Container = styled.div`
  margin-top:${spacing.md};
  border:1px solid ${colors.border.light};
  border-radius:${borderRadius.md};
  overflow:hidden;
`;
const TableHeader = styled.div`
  display:flex;justify-content:space-between;align-items:center;
  padding:${spacing.sm} ${spacing.md};
  background:${colors.neutral[50]};
  border-bottom:1px solid ${colors.border.light};
`;
const Title = styled.span`
  font-size:${typography.fontSize.sm};
  font-weight:${typography.fontWeight.semibold};
  color:${colors.text.primary};
`;
const ButtonGroup = styled.div`display:flex;gap:${spacing.sm};`;
const AllocBtn = styled.button`
  padding:4px 12px;font-size:${typography.fontSize.xs};
  background:${colors.primary.main};color:white;border:none;
  border-radius:${borderRadius.sm};cursor:pointer;
  font-weight:${typography.fontWeight.medium};
  &:hover{opacity:0.9;}
  &:disabled{opacity:0.4;cursor:not-allowed;}
`;
const ClearBtn = styled.button`
  padding:4px 12px;font-size:${typography.fontSize.xs};
  background:transparent;color:${colors.text.secondary};
  border:1px solid ${colors.border.light};
  border-radius:${borderRadius.sm};cursor:pointer;
  &:hover{background:${colors.neutral[100]};}
  &:disabled{opacity:0.4;cursor:not-allowed;}
`;
// Long lists scroll inside the box with the headings fixed, so the totals stay in view.
const TableWrap = styled.div`overflow:auto;max-height:440px;`;
const FilterBar = styled.div`
  display:flex;flex-wrap:wrap;gap:8px;align-items:center;
  padding:8px ${spacing.md};border-bottom:1px solid ${colors.border.light};
`;
const FilterInput = styled.input`
  padding:5px 8px;font-size:${typography.fontSize.sm};font-family:inherit;
  border:1px solid ${colors.border.light};border-radius:${borderRadius.sm};
  color:${colors.text.primary};outline:none;min-width:0;
  &:focus{border-color:${colors.primary.main};}
`;
const FilterSelect = styled.select`
  padding:5px 8px;font-size:${typography.fontSize.sm};font-family:inherit;
  border:1px solid ${colors.border.light};border-radius:${borderRadius.sm};
  color:${colors.text.primary};background:white;
`;
const FilterLabel = styled.label`
  display:flex;align-items:center;gap:4px;
  font-size:${typography.fontSize.xs};color:${colors.text.secondary};
`;
const FilterCount = styled.span`
  margin-left:auto;font-size:${typography.fontSize.xs};color:${colors.text.secondary};white-space:nowrap;
`;
const BillNo = styled.span`
  display:block;font-size:11px;color:${colors.text.secondary};font-weight:normal;
`;
const Table = styled.table`width:100%;border-collapse:collapse;`;
const Th = styled.th`
  text-align:left;padding:8px ${spacing.md};
  background:${colors.neutral[100]};
  font-size:${typography.fontSize.xs};
  font-weight:${typography.fontWeight.semibold};
  color:${colors.text.secondary};
  text-transform:uppercase;letter-spacing:0.5px;
  border-bottom:1px solid ${colors.border.light};
  white-space:nowrap;
  position:sticky;top:0;z-index:1;
  cursor:${props => (props.$click ? 'pointer' : 'default')};
  user-select:none;
`;
const Tr = styled.tr`
  background:${props => props.$allocated ? colors.success.main + '08' : 'transparent'};
  &:not(:last-child) td{border-bottom:1px solid ${colors.neutral[100]};}
  &:hover{background:${colors.neutral[50]};}
`;
const Td = styled.td`
  padding:6px ${spacing.md};font-size:${typography.fontSize.sm};
  color:${colors.text.primary};white-space:nowrap;
`;
const AllocInput = styled.input`
  width:120px;padding:4px 8px;text-align:right;
  border:1px solid ${colors.border.light};border-radius:${borderRadius.sm};
  font-size:${typography.fontSize.sm};color:${colors.text.primary};
  outline:none;
  &:focus{border-color:${colors.primary.main};}
`;
const SmallInput = styled(AllocInput)`width:64px;&:disabled{background:${colors.neutral[50]};cursor:not-allowed;}`;
const MidInput = styled(AllocInput)`width:100px;&:disabled{background:${colors.neutral[50]};cursor:not-allowed;}`;
const InvWhtTag = styled.span`
  margin-left:6px;padding:1px 6px;border-radius:8px;
  font-size:10px;font-weight:${typography.fontWeight.medium};
  color:${colors.warning.dark || '#7c5e00'};background:${colors.warning.main}25;
  cursor:help;
`;
const SummaryRow = styled.div`
  display:flex;gap:${spacing.lg};padding:${spacing.sm} ${spacing.md};
  background:${colors.neutral[50]};border-top:1px solid ${colors.border.light};
  flex-wrap:wrap;
`;
const SummaryItem = styled.div`display:flex;flex-direction:column;gap:2px;`;
const SummaryLabel = styled.span`
  font-size:${typography.fontSize.xs};color:${colors.text.secondary};
`;
const SummaryValue = styled.span`
  font-size:${typography.fontSize.sm};
  font-weight:${typography.fontWeight.semibold};
  color:${props => props.$warning ? colors.error.main : props.$muted ? colors.text.tertiary : colors.text.primary};
`;
const WarningMsg = styled.div`
  padding:${spacing.sm} ${spacing.md};
  background:${colors.error.main}10;color:${colors.error.main};
  font-size:${typography.fontSize.xs};font-weight:${typography.fontWeight.medium};
`;
const EmptyMsg = styled.div`
  padding:${spacing.md};text-align:center;
  color:${colors.text.tertiary};font-size:${typography.fontSize.sm};
  background:${colors.neutral[50]};border-radius:${borderRadius.md};
  margin-top:${spacing.sm};
`;

export default InvoiceAllocationTable;
